import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Assessment,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Info,
  FileDownload,
  Settings,
  Add,
  Refresh,
  PlayArrow,
  Pause,
  Launch,
  Visibility,
  Business,
  CalendarToday,
  AccountBalance,
  Receipt,
  Assignment
} from '@mui/icons-material';
import { format } from 'date-fns';

const TaxuallyDashboard = () => {
  const [company, setCompany] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [filings, setFilings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('monthly');
  const [selectedJurisdictions, setSelectedJurisdictions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const companyId = 29; // This should come from context/auth

      // Load all data in parallel
      const [eligibilityRes, jurisdictionsRes, filingsRes, settingsRes] = await Promise.all([
        fetch(`/api/v1/taxually/eligibility/${companyId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/v1/taxually/jurisdictions/${companyId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/v1/taxually/filings/${companyId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/v1/taxually/settings/${companyId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const eligibilityData = await eligibilityRes.json();
      const jurisdictionsData = await jurisdictionsRes.json();
      const filingsData = await filingsRes.json();
      const settingsData = await settingsRes.json();

      setEligibility(eligibilityData);
      setJurisdictions(jurisdictionsData.supported_jurisdictions || []);
      setFilings(filingsData.filings || []);
      setSettings(settingsData);
      setCompany({ id: companyId });

    } catch (err) {
      setError('Failed to load Taxually dashboard data');
      logger.error('Dashboard loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectTaxually = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/taxually/connect/${company.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Successfully connected to Taxually!');
        await loadDashboardData();
      } else {
        setError(data.detail || 'Failed to connect to Taxually');
      }
    } catch (err) {
      setError('Failed to connect to Taxually');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFiling = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/taxually/file/${company.id}?filing_period=${selectedPeriod}&jurisdiction=${selectedJurisdiction}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Filing submitted successfully! Reference: ${data.taxually_submission_id}`);
        setDialogOpen(false);
        await loadDashboardData();
      } else {
        setError(data.detail || 'Failed to submit filing');
      }
    } catch (err) {
      setError('Failed to submit filing');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSchedule = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/taxually/schedule/${company.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jurisdictions: selectedJurisdictions,
          filing_frequency: selectedFrequency,
          auto_submit: true
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Automated filing schedule created successfully!');
        setScheduleDialogOpen(false);
        await loadDashboardData();
      } else {
        setError(data.detail || 'Failed to create filing schedule');
      }
    } catch (err) {
      setError('Failed to create filing schedule');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      completed: 'success',
      submitted: 'primary',
      processing: 'warning',
      failed: 'error',
      draft: 'default',
      pending: 'info'
    };
    return statusColors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      completed: <CheckCircle color="success" />,
      submitted: <CloudUpload color="primary" />,
      processing: <CircularProgress size={20} />,
      failed: <Error color="error" />,
      draft: <Assignment color="action" />,
      pending: <Schedule color="info" />
    };
    return statusIcons[status] || <Info />;
  };

  if (loading && !company) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Taxually Automated Filing
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Connection Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Business color="primary" />
                <Typography variant="h6">Taxually Connection Status</Typography>
              </Box>
              
              {!eligibility?.taxually_eligible ? (
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Connect your company to Taxually for automated tax filing across 90+ countries
                  </Typography>
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {eligibility?.reason || 'An active GoTruTax plan with filing is required'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Launch />}
                    onClick={handleConnectTaxually}
                    disabled={loading}
                  >
                    Connect to Taxually
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CheckCircle color="success" />
                    <Typography color="success.main" fontWeight="bold">
                      Connected to Taxually
                    </Typography>
                    <Chip 
                      label={eligibility.subscription_level.toUpperCase()} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                  <Typography color="text.secondary">
                    Automated filing available for {jurisdictions.length} jurisdictions
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={4}>
              {eligibility?.taxually_eligible && (
                <Box>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Add />}
                    onClick={() => setDialogOpen(true)}
                    sx={{ mb: 1 }}
                  >
                    File Now
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Schedule />}
                    onClick={() => setScheduleDialogOpen(true)}
                  >
                    Setup Schedule
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {eligibility?.taxually_eligible && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <AccountBalance color="primary" />
                  <Box>
                    <Typography variant="h6">{jurisdictions.length}</Typography>
                    <Typography color="text.secondary">Supported Jurisdictions</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Receipt color="success" />
                  <Box>
                    <Typography variant="h6">
                      {filings.filter(f => f.status === 'completed').length}
                    </Typography>
                    <Typography color="text.secondary">Completed Filings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CloudUpload color="warning" />
                  <Box>
                    <Typography variant="h6">
                      {filings.filter(f => ['submitted', 'processing'].includes(f.status)).length}
                    </Typography>
                    <Typography color="text.secondary">In Progress</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CalendarToday color="info" />
                  <Box>
                    <Typography variant="h6">
                      {settings?.auto_filing_enabled ? 'ON' : 'OFF'}
                    </Typography>
                    <Typography color="text.secondary">Auto Filing</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Filings Table */}
      {eligibility?.taxually_eligible && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Filings
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Jurisdiction</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filings.slice(0, 10).map((filing) => (
                    <TableRow key={filing.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(filing.status)}
                          <Chip 
                            label={filing.status.toUpperCase()} 
                            color={getStatusColor(filing.status)}
                            size="small" 
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {filing.jurisdiction}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {filing.jurisdiction_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{filing.filing_period}</TableCell>
                      <TableCell>
                        <Chip 
                          label={filing.filing_type.replace('_', ' ').toUpperCase()}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        ${filing.tax_liability_amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {filing.submitted_at ? 
                          format(new Date(filing.submitted_at), 'MMM dd, yyyy') : 
                          '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <Button size="small" startIcon={<Visibility />}>
                              View
                            </Button>
                          </Tooltip>
                          {filing.status === 'completed' && (
                            <Tooltip title="Download Reports">
                              <Button size="small" startIcon={<FileDownload />}>
                                Download
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {filings.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  No filings found. Submit your first automated filing to get started.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Filing Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Manual Filing</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Jurisdiction</InputLabel>
              <Select
                value={selectedJurisdiction}
                onChange={(e) => setSelectedJurisdiction(e.target.value)}
                label="Jurisdiction"
              >
                {jurisdictions.map((jurisdiction) => (
                  <MenuItem key={jurisdiction.jurisdiction_code} value={jurisdiction.jurisdiction_code}>
                    {jurisdiction.jurisdiction_name} ({jurisdiction.jurisdiction_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Filing Period"
              placeholder="e.g., 2025-Q1 or 2025-05"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              helperText="Enter the filing period (quarterly: YYYY-Q1, monthly: YYYY-MM)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitFiling}
            disabled={!selectedJurisdiction || !selectedPeriod || loading}
          >
            Submit Filing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Automated Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Setup Automated Filing Schedule</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Filing Frequency</InputLabel>
              <Select
                value={selectedFrequency}
                onChange={(e) => setSelectedFrequency(e.target.value)}
                label="Filing Frequency"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="subtitle1" gutterBottom>
              Select Jurisdictions for Automated Filing:
            </Typography>
            
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
              {jurisdictions.map((jurisdiction) => (
                <FormControlLabel
                  key={jurisdiction.jurisdiction_code}
                  control={
                    <Switch
                      checked={selectedJurisdictions.includes(jurisdiction.jurisdiction_code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedJurisdictions([...selectedJurisdictions, jurisdiction.jurisdiction_code]);
                        } else {
                          setSelectedJurisdictions(
                            selectedJurisdictions.filter(j => j !== jurisdiction.jurisdiction_code)
                          );
                        }
                      }}
                    />
                  }
                  label={`${jurisdiction.jurisdiction_name} (${jurisdiction.jurisdiction_code})`}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSetupSchedule}
            disabled={selectedJurisdictions.length === 0 || loading}
          >
            Create Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxuallyDashboard; 